// Web shell: lobby → permission gate → models loading → run → result.
// Wires Camera + 3 MediaPipe trackers + AggregateFrameSource into the runner.

import {
  GameRunner,
  type GameAudio,
  type GameContext,
  type Microgame,
  type RunResult,
} from '@pose-game/core';
import {
  AggregateFrameSource,
  Camera,
  FaceTracker,
  HandsTracker,
  PoseTracker,
  drawMeshOverlay,
} from '@pose-game/input';
import {
  defaultRun,
  chomp,
  ninjaSlice,
  dodgeTilt,
  squatGate,
  smileHold,
  pointMole,
  freeze,
  castCombo,
  boxing,
  poseTetris,
} from '@pose-game/microgames';

// Catalog used by the lobby grid. Order + label control the menu.
const CATALOG: { game: Microgame; title: string; tag: string }[] = [
  { game: chomp, title: 'Chomp', tag: 'MOUTH' },
  { game: ninjaSlice, title: 'Ninja Slice', tag: 'HAND' },
  { game: dodgeTilt, title: 'Dodge Tilt', tag: 'HEAD' },
  { game: squatGate, title: 'Squat Gate', tag: 'BODY' },
  { game: smileHold, title: 'Smile Hold', tag: 'FACE' },
  { game: pointMole, title: 'Point Mole', tag: 'AIM' },
  { game: freeze, title: 'Freeze', tag: 'STILL' },
  { game: castCombo, title: 'Cast Combo', tag: 'COMBO' },
  { game: boxing, title: 'Boxing', tag: 'PUNCH' },
  { game: poseTetris, title: 'Pose Tetris', tag: 'DUMBBELL' },
];

// ─── DOM ───────────────────────────────────────────────────────────────

const canvas = document.querySelector<HTMLCanvasElement>('#stage')!;
const lobby = document.querySelector<HTMLElement>('#lobby')!;
const result = document.querySelector<HTMLElement>('#result')!;
const playBtn = document.querySelector<HTMLButtonElement>('#play')!;
const againBtn = document.querySelector<HTMLButtonElement>('#again')!;
const menuGrid = document.querySelector<HTMLElement>('#menu-grid')!;
const resultTitle = document.querySelector<HTMLElement>('#result-title')!;
const resultDetail = document.querySelector<HTMLElement>('#result-detail')!;

const ctx = canvas.getContext('2d')!;

// Microgames use `canvas.width` / `canvas.height` directly, so keep the
// backing buffer in CSS pixels (no DPR scaling). High-DPI sharpness can come
// back later via setTransform if we also patch every microgame.
function fitCanvas(): void {
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  canvas.style.width = `${window.innerWidth}px`;
  canvas.style.height = `${window.innerHeight}px`;
}
fitCanvas();
window.addEventListener('resize', fitCanvas);

// ─── Audio ─────────────────────────────────────────────────────────────

const bgm = new Audio('/assets/audio/bgm.opus');
bgm.loop = true;
bgm.volume = 0.35;
bgm.preload = 'auto';

const audio: GameAudio = makeProceduralAudio();

// ─── Input layer ───────────────────────────────────────────────────────

const camera = new Camera({ width: 640, height: 480, facingMode: 'user' });
const face = new FaceTracker({ camera });
const hands = new HandsTracker({ camera });
const pose = new PoseTracker({ camera });
const frameSource = new AggregateFrameSource({ camera, face, hands, pose });

let booted = false;

// ─── Lobby / loading UI ────────────────────────────────────────────────

function showLobbyState(text: string, disabled: boolean): void {
  playBtn.textContent = text;
  playBtn.disabled = disabled;
  playBtn.style.opacity = disabled ? '0.6' : '1';
}

// ─── Game context ──────────────────────────────────────────────────────

const game: GameContext = {
  canvas,
  ctx,
  audio,
  rng: Math.random,
};

async function boot(): Promise<void> {
  if (booted) return;
  showLobbyState('REQUESTING CAMERA…', true);
  try {
    await camera.start();
  } catch (err) {
    console.error('[main] camera failed', err);
    showLobbyState('CAMERA DENIED — TAP TO RETRY', false);
    return;
  }
  showLobbyState('LOADING MODELS…', true);
  try {
    await Promise.all([face.load(), hands.load(), pose.load()]);
  } catch (err) {
    console.error('[main] model load failed', err);
    showLobbyState('LOAD FAILED — TAP TO RETRY', false);
    return;
  }
  face.start();
  hands.start();
  pose.start();
  booted = true;
}

async function startRun(microgames: Microgame[] = defaultRun): Promise<void> {
  await boot();
  if (!booted) return;
  lobby.classList.add('hidden');
  result.classList.add('hidden');

  try {
    void bgm.play().catch(() => {});
  } catch {
    /* autoplay restrictions — fine */
  }

  const runner = new GameRunner({
    microgames,
    context: game,
    frameSource,
    overlayDraw: drawMeshOverlay,
    onFinish(r: RunResult) {
      showResult(r);
    },
  });
  runner.start();
}

function renderMenu(): void {
  menuGrid.innerHTML = '';
  for (let i = 0; i < CATALOG.length; i++) {
    const entry = CATALOG[i]!;
    const btn = document.createElement('button');
    btn.className = 'menu-card';
    btn.type = 'button';
    btn.innerHTML = `<span class="num">${i + 1}</span><span class="title">${entry.title}</span><span class="sub">${entry.tag}</span>`;
    btn.addEventListener('click', () => {
      void startRun([entry.game]);
    });
    menuGrid.appendChild(btn);
  }
}
renderMenu();

function showResult(r: RunResult): void {
  bgm.pause();
  resultTitle.textContent = `SCORE ${r.totalScore}`;
  resultDetail.textContent = `${r.winCount} of ${r.microgames.length} cleared • ${r.durationSec.toFixed(1)}s`;
  result.classList.remove('hidden');
}

playBtn.addEventListener('click', () => {
  void startRun();
});
againBtn.addEventListener('click', () => {
  lobby.classList.remove('hidden');
  result.classList.add('hidden');
});

console.log('[pose-game] chassis booted. real input layer wired.');

// ─── Procedural SFX (WebAudio) ─────────────────────────────────────────
//
// Tiny sine + noise synth. Mirrors the chomp-ios ChompAudio shapes but kept
// to ~80 lines so it fits in the shell.

function makeProceduralAudio(): GameAudio {
  let actx: AudioContext | null = null;
  function ensure(): AudioContext | null {
    if (actx == null) {
      try {
        const C = (window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext) as
          | typeof AudioContext
          | undefined;
        if (C != null) actx = new C();
      } catch {
        return null;
      }
    }
    if (actx?.state === 'suspended') void actx.resume();
    return actx;
  }

  function sine(freq: number, dur: number, gain = 0.18, glide = 0): void {
    const ac = ensure();
    if (ac == null) return;
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.frequency.setValueAtTime(freq, ac.currentTime);
    if (glide !== 0)
      osc.frequency.linearRampToValueAtTime(freq + glide, ac.currentTime + dur);
    g.gain.setValueAtTime(0, ac.currentTime);
    g.gain.linearRampToValueAtTime(gain, ac.currentTime + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);
    osc.connect(g).connect(ac.destination);
    osc.start();
    osc.stop(ac.currentTime + dur);
  }

  function noise(dur: number, gain = 0.25): void {
    const ac = ensure();
    if (ac == null) return;
    const bufSize = Math.max(1, Math.floor(ac.sampleRate * dur));
    const buf = ac.createBuffer(1, bufSize, ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufSize);
    const src = ac.createBufferSource();
    src.buffer = buf;
    const g = ac.createGain();
    g.gain.value = gain;
    src.connect(g).connect(ac.destination);
    src.start();
  }

  function arpeggio(freqs: number[], stepSec = 0.09, gain = 0.18): void {
    for (let i = 0; i < freqs.length; i++) {
      const f = freqs[i];
      if (f == null) continue;
      setTimeout(() => sine(f, 0.18, gain), i * stepSec * 1000);
    }
  }

  return {
    beep: () => sine(880, 0.08, 0.15),
    pop: () => {
      sine(520, 0.1, 0.18, -200);
      noise(0.04, 0.15);
    },
    thud: () => {
      sine(120, 0.35, 0.45, -50);
      noise(0.25, 0.2);
    },
    win: () => arpeggio([523.25, 659.25, 783.99], 0.08, 0.22),
    lose: () => arpeggio([440, 349.23, 293.66, 220], 0.14, 0.2),
  };
}
